/**
 * @fileoverview CRM contact aggregation
 *
 * Purpose:
 * - Walk the eight summarization-agent-populated tables that drive the
 *   CRM (corporateEntries, relatedPartyEntries, securityInterestEntries,
 *   employeeEntries, liabilityEntries, loanEntries, atoSroEntries,
 *   assetEntries) and project every contact-shaped row into a flat
 *   `ContactCandidate[]` consumable by the reconcile pipeline.
 * - Pure helper — no writes, no auth checks. Callers (queries / internal
 *   mutations) are expected to perform their own access control before
 *   invoking this.
 *
 * Dependencies:
 * - QueryCtx / MutationCtx for `ctx.db` reads
 *
 * Used by:
 * - convex/contacts/reconcile.ts (rebuilds jobContacts on every run)
 *
 * Notes:
 * - This file replaces the inline projection logic that lived in v1
 *   convex/jobs/contacts.ts. The dedup / grouping logic now lives in
 *   reconcile.ts; this file is purely the per-source projector.
 */

import type { QueryCtx, MutationCtx } from "@cvx/_generated/server";
import type { Id } from "@cvx/_generated/dataModel";
import { sumEmployeeEntitlements } from "@cvx/lib/employeeEntitlements";
import type {
  ContactRole,
  ContactSourceKind,
  ExposureDirection,
} from "@cvx/contacts/jobContacts/schema";
import type { ContactType } from "@cvx/contacts/firmContacts/schema";
import { isPersonalInsolvencyAppointmentType } from "@cvx/lib/appointmentTypes";
import {
  appointeeRoleLabel,
  buildExternalAdminSuggestionResolver,
  isExternalAdminAppointeeRow,
  type ExternalAdminSuggestion,
} from "./externalAdminDetection";
import {
  resolveBankruptPersonName,
  stripBankruptEstatePrefix,
} from "@cvx/lib/bankruptName";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** One agent-source row projected for the reconcile pipeline. */
export type ContactCandidate = {
  /** Stable hash for grouping: ACN > ABN > normalised name. */
  dedupKey: string;
  /**
   * Which basis was used to compute `dedupKey`. Used by reconcile to
   * surface a `mergeConfidence` on the final `jobContacts` row.
   */
  matchBasis: "acn" | "abn" | "name";
  displayName: string;
  contactType: ContactType;
  abn?: string;
  acn?: string;
  source: {
    kind: ContactSourceKind;
    /** _id of the originating row. */
    sourceId: string;
    /** Canonical role enum for filtering and document generation. */
    role: ContactRole;
    roleLabel: string;
    detail?: string;
    amount?: number;
    exposureDirection?: ExposureDirection;
  };
  address?: {
    line1?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    sourceLabel: string;
  };
  /**
   * Contact details attributed to THIS specific party (not the wider
   * group). Currently sourced from the Security Interests projection —
   * a PPSR "Address for Service" block often carries an email/phone
   * attributed to one member of a joint registration; capturing it on
   * the candidate lets reconcile seed the correct `jobContacts` row on
   * first-create rather than relying on the downstream enrichment agent
   * (which correctly refuses to attribute a joint-member email to the
   * group lead). Reconcile only ever seeds these on CREATE — a
   * practitioner edit is never overwritten.
   */
  email?: string;
  phone?: string;
  contactName?: string;
  /**
   * Suggest-only hint that this company is in external administration,
   * derived from the corporate register (an appointee listed against the
   * entity, or a status in its detail). Reconcile persists it on
   * `jobContacts.externalAdminSuggestion`; the practitioner confirms or
   * dismisses it in the detail panel. Never writes the authoritative fields.
   */
  externalAdminSuggestion?: ExternalAdminSuggestion;
  /**
   * ISO `yyyy-MM-dd`. Only the bankrupt carries one — projected from
   * `jobs.bankruptDateOfBirth` so the Bankrupt contact is born with the DOB
   * captured at job setup (MOR-2342). Reconcile fills it only when the row
   * has none, so a practitioner-entered DOB is never overwritten.
   */
  dateOfBirth?: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ORG_NAME_SUFFIXES = [
  "pty ltd",
  "pty. ltd.",
  "pty limited",
  "ltd",
  "limited",
  "inc",
  "incorporated",
  "llc",
  "trust",
  "atf",
];

/**
 * Recover the agent-table row _id from a `sourceLinks[].sourceId`.
 *
 * Most sourceIds ARE the row _id, but two aggregator paths append a stable
 * fragment so multiple contacts can share one source row: joint secured
 * parties (`<rowId>#joint:<name>`) and legacy compound-name splits
 * (`<rowId>#<name>`). Every `db.get` on a sourceId MUST go through this
 * helper — a suffixed id throws "Unable to decode ID" otherwise.
 */
export function sourceRowIdFromLinkId(sourceId: string): string {
  const hash = sourceId.indexOf("#");
  return hash === -1 ? sourceId : sourceId.slice(0, hash);
}

/** Strip non-digits from an identifier (ABN/ACN). Returns undefined when empty. */
export function normaliseId(
  raw: string | undefined | null,
): string | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, "");
  return digits.length > 0 ? digits : undefined;
}

/**
 * Lower-cased, whitespace-normalised, suffix-stripped name used as the
 * fall-back dedup key when no ABN/ACN is available. Imperfect on purpose —
 * we surface a "low" merge confidence on rows that hit this path so the
 * practitioner can spot fuzzy merges (e.g. two different "John Smith"
 * rows collapsing into one).
 */
export function normaliseName(raw: string | undefined | null): string {
  if (!raw) return "";
  // MOR-2342 — "Bankrupt Estate of Alex Morgan" IS Alex Morgan for dedup
  // purposes: the estate heading from a certificate of appointment and the
  // person named on an identity document must land on one Bankrupt contact.
  let n = stripBankruptEstatePrefix(raw).toLowerCase().trim();
  n = n.replace(/\s+/g, " ");
  n = n.replace(/[.,;]+$/g, "").trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const suffix of ORG_NAME_SUFFIXES) {
      if (n.endsWith(" " + suffix)) {
        n = n.slice(0, n.length - suffix.length - 1).trim();
        changed = true;
      } else if (n === suffix) {
        n = "";
        changed = true;
      }
    }
    n = n.replace(/[.,;]+$/g, "").trim();
  }
  return n;
}

/**
 * Build a dedup key for a candidate using ACN > ABN > normalised name.
 * Returns the key plus which basis was used so we can score merge confidence.
 */
export function buildDedupKey(args: {
  name: string;
  abn?: string;
  acn?: string;
}): { key: string; basis: "acn" | "abn" | "name" } {
  if (args.acn) return { key: `acn:${args.acn}`, basis: "acn" };
  if (args.abn) return { key: `abn:${args.abn}`, basis: "abn" };
  const n = normaliseName(args.name);
  return { key: `name:${n}`, basis: "name" };
}

function relatedPartyRoleLabel(category: string): string {
  switch (category) {
    case "director":
      return "Director";
    case "former_director":
      return "Former Director";
    case "shareholder":
      return "Shareholder";
    case "related_body_corporate":
      return "Related Body Corporate";
    case "director_relative":
      return "Director's Relative";
    case "spouse_of_director":
      return "Spouse of Director";
    case "controlled_entity":
      return "Controlled Entity";
    case "associate":
      return "Associate";
    case "unknown_associated_party":
      return "Associated Party";
    case "spouse":
      return "Spouse of Bankrupt";
    case "de_facto_partner":
      return "De Facto Partner of Bankrupt";
    case "former_spouse":
      return "Former Spouse of Bankrupt";
    case "former_de_facto_partner":
      return "Former De Facto Partner of Bankrupt";
    case "relative":
      return "Relative of Bankrupt";
    default:
      return "Related Party";
  }
}

function corporateCategoryRoleLabel(
  category: string,
  entityType: string,
): string {
  switch (category) {
    case "officers":
      return "Officer";
    case "shareholders":
      return "Shareholder";
    case "related_companies":
      return "Related Company";
    case "subsidiaries":
      return "Subsidiary";
    case "trusts":
      return "Trust";
    case "secured_parties":
      return "Secured Party";
    case "professionals":
      return "Professional Adviser";
    case "other":
    default:
      return entityType === "trust" ? "Trust" : "Other Entity";
  }
}

function corporateEntityTypeToContactType(t: string): ContactType {
  if (t === "person") return "person";
  if (t === "company") return "organisation";
  if (t === "trust") return "trust";
  if (t === "organisation") return "organisation";
  return "unknown";
}

function relatedPartyTypeToContactType(t: string): ContactType {
  if (t === "individual") return "person";
  if (t === "company") return "organisation";
  if (t === "trust") return "trust";
  return "unknown";
}

/**
 * Detect and split compound secured-party names that the AI agent writes
 * when a PPSR registration lists multiple parties under a single entry.
 *
 * Handles two patterns:
 *  1. "Multiple Xs (A / B / C)" — extracts the names inside the parens
 *  2. "A / B / C" — splits on " / " when the result has 2+ meaningful parts
 *
 * Returns undefined when the name is a single party (no splitting needed).
 *
 * LEGACY / BACKSTOP PATH (MOR-708): the Security Interests agent is now
 * prompted to represent multiple entities grouped under one PPSR
 * registration as a single "X Group" entry (one contact, one set of
 * contact details), so new rows should never carry compound names.
 * "X Group" names contain no slashes and never match either pattern, so
 * grouped rows pass through this splitter untouched. The splitter is
 * deliberately retained for (a) rows written before that prompt change
 * and (b) any future row where the agent slips and writes a compound name
 * anyway — split contacts without identifiers beat one mangled contact.
 * Do not extend the split patterns to catch more cases; fix the writing
 * agent's prompt instead so grouping is preserved at the source.
 */
export function splitCompoundPartyName(raw: string): string[] | undefined {
  // Pattern 1: "Multiple <category> (A / B / C)"
  const wrappedMatch = raw.match(/^Multiple\s+\w+\s*\((.+)\)\s*$/i);
  if (wrappedMatch) {
    const inner = wrappedMatch[1];
    const parts = inner.split(/\s*\/\s*/).filter((p) => p.length > 0);
    if (parts.length > 1) return parts;
  }

  // Pattern 2: bare slash-separated list — only split if 2+ parts and the
  // name doesn't look like a legitimate "X / Y" trading name (<= 2 parts
  // where both are short can be a real entity like "Finance One / Two").
  // Conservative: require 3+ parts to split without the "Multiple" wrapper.
  const parts = raw.split(/\s*\/\s*/).filter((p) => p.length > 0);
  if (parts.length >= 3) return parts;

  return undefined;
}

function loanDirectionLabel(direction: string): string {
  if (direction === "Loan_Granted") return "Loan counterparty (lent to)";
  if (direction === "Loan_Taken") return "Loan counterparty (borrowed from)";
  return "Loan counterparty";
}

function loanExposureDirection(direction: string): ExposureDirection {
  return direction === "Loan_Granted" ? "debtor" : "creditor";
}

function loanDirectionRole(direction: string): ContactRole {
  return direction === "Loan_Granted" ? "borrower" : "lender";
}

const DIRECTOR_KEYWORDS = /\bdirector\b/i;
const SECRETARY_KEYWORDS = /\bsecretary\b/i;

/**
 * Map a corporate entry's category + detail + isCurrent into a canonical
 * contact role. Parses the free-text `detail` field for director/secretary
 * keywords when the category is the broad "officers" bucket.
 */
function corporateCategoryToRole(
  category: string,
  detail: string | undefined,
  isCurrent: boolean | undefined,
): ContactRole {
  switch (category) {
    case "officers": {
      if (detail && SECRETARY_KEYWORDS.test(detail)) return "secretary";
      if (detail && DIRECTOR_KEYWORDS.test(detail)) {
        return isCurrent === false ? "former_director" : "director";
      }
      return isCurrent === false ? "former_director" : "officer";
    }
    case "shareholders":
      return "shareholder";
    case "related_companies":
    case "subsidiaries":
    case "trusts":
      return "related_entity";
    case "secured_parties":
      return "secured_creditor";
    case "professionals":
      return "professional";
    default:
      return "other";
  }
}

/**
 * Role/label override for the job's primary entity. In a bankruptcy the
 * subject (`isPrimaryEntity: true`, seeded by the corporate agent with the
 * catch-all `category: "other"`) is "the bankrupt" rather than a generic
 * "Other Entity". Returns `null` for any non-bankruptcy primary entity or
 * non-primary row, leaving the default corporate mapping in place.
 */
function primaryEntityRoleOverride(
  isPrimaryEntity: boolean | undefined,
  appointmentType: string | undefined,
): { role: ContactRole; roleLabel: string } | null {
  if (!isPrimaryEntity) return null;
  // A Part X debtor rides the same role: `bankrupt` is the contact role for
  // the individual whose estate is administered (MOR-1838).
  if (!isPersonalInsolvencyAppointmentType(appointmentType)) return null;
  return { role: "bankrupt", roleLabel: "Bankrupt" };
}

function relatedPartyCategoryToRole(category: string): ContactRole {
  switch (category) {
    case "director":
      return "director";
    case "former_director":
      return "former_director";
    case "shareholder":
      return "shareholder";
    case "related_body_corporate":
    case "controlled_entity":
      return "related_entity";
    case "director_relative":
    case "spouse_of_director":
    case "associate":
    case "unknown_associated_party":
    case "spouse":
    case "de_facto_partner":
    case "former_spouse":
    case "former_de_facto_partner":
    case "relative":
      return "related_individual";
    default:
      return "other";
  }
}

// ---------------------------------------------------------------------------
// Aggregation entry point
// ---------------------------------------------------------------------------

/**
 * Read every active row from the eight CRM-relevant agent-source tables
 * and project them into a flat list of `ContactCandidate`s.
 *
 * Idempotent and side-effect-free. Caller is responsible for auth.
 */
export async function aggregateContactCandidates(
  ctx: QueryCtx | MutationCtx,
  jobId: Id<"jobs">,
): Promise<ContactCandidate[]> {
  const [
    corporateRows,
    relatedPartyRows,
    securityRowsRaw,
    employeeRowsRaw,
    liabilityRowsRaw,
    loanRows,
    atoSroRows,
    assetRows,
  ] = await Promise.all([
    ctx.db
      .query("corporateEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
    ctx.db
      .query("relatedPartyEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
    ctx.db
      .query("securityInterestEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
    ctx.db
      .query("employeeEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
    ctx.db
      .query("liabilityEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
    ctx.db
      .query("loanEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
    ctx.db
      .query("atoSroEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
    ctx.db
      .query("assetEntries")
      .withIndex("by_job_and_status", (q) =>
        q.eq("jobId", jobId).eq("status", "active"),
      )
      .collect(),
  ]);

  // Excluded creditors/employees are dropped from CRM contact exposure so an
  // excluded row (e.g. a discharged-PPSR secured party) does not surface a
  // contact or count its amount towards a contact's exposure.
  const securityRows = securityRowsRaw.filter((e) => e.isExcluded !== true);
  const employeeRows = employeeRowsRaw.filter((e) => e.isExcluded !== true);
  const liabilityRows = liabilityRowsRaw.filter((e) => e.isExcluded !== true);

  const candidates: ContactCandidate[] = [];

  const job = await ctx.db.get(jobId);
  const appointmentType = job?.appointmentType;

  // External administration (EXAD) handling for the corporate register:
  // ASIC extracts list liquidators / administrators / receivers in the same
  // officeholders table as directors, so the agent records them as
  // `officers`. They are appointed OVER a company, not part of it — the
  // corporate summary already excludes them from the structure map and the
  // CRM must agree, so an appointee is never projected as a director /
  // officer. It becomes a `professional` contact instead, and the company it
  // administers receives a suggest-only EXAD hint (see externalAdminDetection).
  const externalAdminSuggestionFor =
    buildExternalAdminSuggestionResolver(corporateRows);

  for (const row of corporateRows) {
    const abn = normaliseId(row.abn);
    const acn = normaliseId(row.acn);
    const primaryOverride = primaryEntityRoleOverride(
      row.isPrimaryEntity,
      appointmentType,
    );
    const isAppointee = isExternalAdminAppointeeRow(row);
    // MOR-2342 — the bankrupt is an individual known by their own name. The
    // corporate agent seeds the primary row from the job's estate heading
    // ("Bankrupt Estate of …"), so project the person instead: the Bankrupt
    // contact, and every document bound to it, carries the person's name
    // only. Falls back to the row's own name with the heading removed.
    const bankruptName = primaryOverride
      ? (resolveBankruptPersonName({
          bankruptName: job?.bankruptName,
          entityName: row.entityName,
        }) ?? row.entityName)
      : undefined;
    const displayName = bankruptName ?? row.entityName;
    const dedup = buildDedupKey({ name: displayName, abn, acn });
    candidates.push({
      dedupKey: dedup.key,
      matchBasis: dedup.basis,
      displayName,
      contactType: bankruptName
        ? "person"
        : corporateEntityTypeToContactType(row.entityType),
      abn,
      acn,
      dateOfBirth: bankruptName
        ? (job?.bankruptDateOfBirth ?? undefined)
        : undefined,
      source: {
        kind: "corporate",
        sourceId: row._id,
        role: isAppointee
          ? "professional"
          : (primaryOverride?.role ??
            corporateCategoryToRole(row.category, row.detail, row.isCurrent)),
        roleLabel: isAppointee
          ? appointeeRoleLabel(row.detail)
          : (primaryOverride?.roleLabel ??
            corporateCategoryRoleLabel(row.category, row.entityType)),
        detail: row.detail,
      },
      externalAdminSuggestion: externalAdminSuggestionFor(row),
    });
  }

  for (const row of relatedPartyRows) {
    const abn = normaliseId(row.abn);
    const acn = normaliseId(row.acn);
    const dedup = buildDedupKey({ name: row.legalName, abn, acn });
    const address = row.primaryAddress
      ? {
          line1: row.primaryAddress.line1,
          suburb: row.primaryAddress.suburb,
          state: row.primaryAddress.state,
          sourceLabel: "Related party — primary address",
        }
      : undefined;
    candidates.push({
      dedupKey: dedup.key,
      matchBasis: dedup.basis,
      displayName: row.legalName,
      contactType: relatedPartyTypeToContactType(row.partyType),
      abn,
      acn,
      source: {
        kind: "related_party",
        sourceId: row._id,
        role: relatedPartyCategoryToRole(row.relationshipCategory),
        roleLabel: relatedPartyRoleLabel(row.relationshipCategory),
        detail: row.summary,
      },
      address,
    });
  }

  for (const row of securityRows) {
    // A registration held jointly (corporate groups, MOR-708) carries the
    // lead party in `securedParty` and every other party in
    // `jointSecuredParties`. Each party projects to its OWN candidate with
    // its own identifiers — grouping back into one commercial relationship
    // happens in the contact-groups layer, not by collapsing entities here.
    const leadAddress = row.securedParty.addresses[0];
    const parties = [row.securedParty, ...(row.jointSecuredParties ?? [])];

    for (const [partyIndex, sp] of parties.entries()) {
      const idType = (sp.identifierType ?? "").toLowerCase();
      const rawAbn = idType.includes("abn")
        ? normaliseId(sp.identifier)
        : undefined;
      const rawAcn = idType.includes("acn")
        ? normaliseId(sp.identifier)
        : undefined;

      // Joint registrations usually publish one shared address for
      // service — fall back to the lead party's address so every member
      // contact carries usable contact details.
      const firstAddress = sp.addresses[0] ?? leadAddress;
      const address = firstAddress
        ? {
            line1: firstAddress.line1,
            suburb: firstAddress.suburb,
            state: firstAddress.state,
            postcode: firstAddress.postcode,
            sourceLabel: `Security interest — ${firstAddress.type} address`,
          }
        : undefined;

      const splitNames = splitCompoundPartyName(sp.name);
      const partyNames = splitNames ?? [sp.name];

      for (const partyName of partyNames) {
        const abn = splitNames ? undefined : rawAbn;
        const acn = splitNames ? undefined : rawAcn;
        const dedup = buildDedupKey({ name: partyName, abn, acn });

        // The sourceId must be distinct per party so the reconciler's
        // source-link index keeps each one as its own contact rather than
        // collapsing them all onto the first match. The lead party keeps
        // the bare row._id (stable for pre-existing links); joint parties
        // and legacy compound-name splits get a stable suffix.
        const sourceId =
          partyIndex === 0
            ? splitNames
              ? `${row._id}#${normaliseName(partyName)}`
              : row._id
            : `${row._id}#joint:${normaliseName(partyName)}`;

        // Contact details on the Address for Service block are attributed
        // per-party by the SI agent (see the agent manifest, "Address for
        // Service — email / phone / contact name attribution"). Only
        // forward them when this projection is going to a single party —
        // if we split a legacy compound name, we can't tell which sub-name
        // a shared email/phone belongs to.
        const email = splitNames ? undefined : sp.email;
        const phone = splitNames ? undefined : sp.phone;
        const contactName = splitNames ? undefined : sp.contactName;

        candidates.push({
          dedupKey: dedup.key,
          matchBasis: dedup.basis,
          displayName: partyName,
          contactType: "organisation",
          abn,
          acn,
          source: {
            kind: "security_interest",
            sourceId,
            role: "secured_creditor",
            roleLabel: "Secured Party",
            detail: row.notes,
          },
          address,
          email,
          phone,
          contactName,
        });
      }
    }
  }

  for (const row of employeeRows) {
    const dedup = buildDedupKey({ name: row.name });
    const sum = sumEmployeeEntitlements(row.entitlements);
    const entAmount: number | undefined = sum > 0 ? sum : undefined;
    candidates.push({
      dedupKey: dedup.key,
      matchBasis: dedup.basis,
      displayName: row.name,
      contactType: row.isCompanyEntity ? "organisation" : "person",
      source: {
        kind: "employee",
        sourceId: row._id,
        role: "employee",
        roleLabel: "Employee",
        detail: row.role,
        amount: entAmount,
        exposureDirection: entAmount ? "creditor" : undefined,
      },
    });
  }

  for (const row of liabilityRows) {
    const splitNames = splitCompoundPartyName(row.creditorName);
    const partyNames = splitNames ?? [row.creditorName];

    for (const partyName of partyNames) {
      const dedup = buildDedupKey({ name: partyName });
      const sourceId = splitNames
        ? `${row._id}#${normaliseName(partyName)}`
        : row._id;
      candidates.push({
        dedupKey: dedup.key,
        matchBasis: dedup.basis,
        displayName: partyName,
        contactType: "organisation",
        source: {
          kind: "liability",
          sourceId,
          role: "unsecured_creditor",
          roleLabel: "Creditor",
          detail: row.notes,
          amount: splitNames ? undefined : row.amount,
          exposureDirection: !splitNames && row.amount ? "creditor" : undefined,
        },
      });
    }
  }

  for (const row of loanRows) {
    const splitNames = splitCompoundPartyName(row.counterpartyName);
    const partyNames = splitNames ?? [row.counterpartyName];

    for (const partyName of partyNames) {
      const dedup = buildDedupKey({ name: partyName });
      const sourceId = splitNames
        ? `${row._id}#${normaliseName(partyName)}`
        : row._id;
      candidates.push({
        dedupKey: dedup.key,
        matchBasis: dedup.basis,
        displayName: partyName,
        contactType: "unknown",
        source: {
          kind: "loan",
          sourceId,
          role: loanDirectionRole(row.direction),
          roleLabel: loanDirectionLabel(row.direction),
          detail: row.notes,
          amount: splitNames ? undefined : row.outstandingBalance,
          exposureDirection:
            !splitNames && row.outstandingBalance
              ? loanExposureDirection(row.direction)
              : undefined,
        },
      });
    }
  }

  for (const row of atoSroRows) {
    // Authorities are well-defined enums; key on the canonical authority
    // value so multiple snapshots of the same regulator collapse cleanly.
    candidates.push({
      dedupKey: `authority:${row.authority}`,
      matchBasis: "name",
      displayName: row.authorityLabel,
      contactType: "authority",
      source: {
        kind: "ato_sro",
        sourceId: row._id,
        role: "tax_authority",
        roleLabel: "Tax Authority",
        detail: row.notes,
        amount: row.runningBalance?.amount,
        exposureDirection: row.runningBalance?.amount ? "creditor" : undefined,
      },
    });
  }

  for (const row of assetRows) {
    for (const valuation of row.valuations) {
      if (!valuation.directorName) continue;
      const dedup = buildDedupKey({ name: valuation.directorName });
      candidates.push({
        dedupKey: dedup.key,
        matchBasis: dedup.basis,
        displayName: valuation.directorName,
        contactType: "person",
        source: {
          kind: "asset_valuation",
          sourceId: row._id,
          role: "director",
          roleLabel: "Asset Valuer (Director)",
          detail: row.description,
          amount: valuation.value,
        },
      });
    }
  }

  return candidates;
}
